// Oberflächen-Manifest eines Moduls: `export default defineWebModule({...})` in modules/<id>/web/index.ts.
// Die Hülle baut daraus Router, Seitenleiste, untere Leiste (Telefon), Befehlspalette, Startseite und die
// Verknüpfungen zwischen Datensätzen. Einträge ohne Recht oder mit abgeschalteter Funktion erscheinen nicht –
// maßgeblich bleibt die Prüfung auf dem Server.
//
// Diese Datei und jedes Manifest müssen in Node ladbar sein (scripts/gen-registry.ts importiert sie):
// Seiten und Widgets NUR über `lazy: () => import('./pages/…')`, keine CSS-/Bild-Importe im Manifest.
import type { AnyActionContract, InputArg, OutputOf, PermissionKey, SearchHit } from '@shared/contract'
import type { ComponentType } from 'react'

/** Lucide-Icon oder gleich gebaute Komponente: `import { Boxes } from 'lucide-react'` */
export type IconComponent = ComponentType<{ className?: string; size?: number | string; strokeWidth?: number | string }>

/** Gruppen der Seitenleiste in fester Reihenfolge (design-system §3.2). 'top' = ohne Gruppe (Start, Aufgaben). */
export const NAV_SECTIONS = [
  { key: 'top', label: '' },
  { key: 'items', label: 'Artikel & Preise' },
  { key: 'stock', label: 'Lager' },
  { key: 'purchasing', label: 'Einkauf' },
  { key: 'sales', label: 'Verkauf' },
  { key: 'finance', label: 'Finanzen' },
  { key: 'pos', label: 'Kasse' },
  { key: 'deposit', label: 'Leergut' },
  { key: 'reports', label: 'Auswertungen' },
  { key: 'settings', label: 'Einstellungen' },
] as const

export type NavSection = (typeof NAV_SECTIONS)[number]['key']

/**
 * Funktionsschalter eines Eintrags: einer – oder mehrere, die ALLE an sein müssen (Chargen im Lager: ['lots', 'stock']).
 * „!schalter“ verlangt, dass der Schalter AUS ist (Umlagerungen unter „Verkauf“ ohne Bestandsführung) – nur für die
 * Oberfläche, nicht für Verträge.
 */
export type FeatureGate = string | readonly string[]

interface Gate {
  /** fehlt das Recht, erscheint der Eintrag nicht */
  permission?: PermissionKey
  /** Funktionsschalter, der an sein muss (mehrere: alle) */
  feature?: FeatureGate
}

export interface WebRoute extends Gate {
  /** englischer, stabiler Pfad (design-system §3.2): '/stock/transfers/:id' */
  path: string
  /** Seite wird nachgeladen; das Modul exportiert die Seitenkomponente als default */
  lazy: () => Promise<{ default: ComponentType }>
  /** deutsch, für Titelzeile und Verlauf */
  title: string
  /**
   * 'app' (Standard): im App-Rahmen · 'fullscreen': verlässt den Rahmen (Kasse, Scan-Ablauf) · 'public': VOR der
   * Anmeldung erreichbar, ohne Rahmen und ohne Sitzung (Einladung annehmen) – Recht und Funktionsschalter gelten dort nicht
   */
  layout?: 'app' | 'fullscreen' | 'public'
}

export interface NavBadge<C extends AnyActionContract = AnyActionContract> {
  /** lesende Aktion, deren Ergebnis ein Feld `count: number` hat – nur Dinge, die eine Handlung erfordern */
  query: C
  input?: InputArg<C>
  tone?: 'neutral' | 'accent' | 'warning' | 'danger'
}

/** Plätze der unteren Leiste, die ein Modul beanspruchen kann (NavEntry.mobile). */
export type BottomBarSlot = 'work' | 'lookup'

export interface NavEntry extends Gate {
  section: NavSection
  /** deutsch: „Bestand“ */
  label: string
  icon: IconComponent
  to: string
  /** Reihenfolge innerhalb der Gruppe (aufsteigend); Abstände von 10 lassen Platz */
  order?: number
  /**
   * Platz in der unteren Leiste am Telefon – fünf Plätze: Start · 2 · Scannen · 4 · Mehr (design-system §3.9).
   *   'work' (oder true)  Platz 2: wo die Rolle arbeitet (Kasse, Lieferungen, Artikel) – frei bleibt er „Aufgaben“
   *   'lookup'            Platz 4: wo sie nachschlägt (Bestand, Belege, Leergut)      – frei bleibt er „Suche“
   * Je Platz gewinnt der erste Eintrag in der Reihenfolge der Navigation, den die Person sehen darf: Die Belegung
   * je Rolle ergibt sich aus den Rechten. Alles Übrige steht unter „Mehr“ – die Suche dort immer, auch wenn
   * Platz 4 vergeben ist. Start, Scannen und Mehr vergibt die Hülle nicht.
   *
   * Mit `{ slot, write }` nennt der Eintrag das Recht, mit dem man hier ARBEITET (schreibt, bestätigt): Wer es hat,
   * bekommt diesen Eintrag vor allen, die er nur lesen darf – der Fahrer sieht „Lieferungen“ statt „Artikel“.
   * Reihenfolge je Platz: Einträge mit erfülltem `write` · Einträge ohne Angabe · Einträge mit fehlendem `write`.
   */
  mobile?: boolean | BottomBarSlot | { slot: BottomBarSlot; write: PermissionKey }
  badge?: NavBadge
}

/** Verweis eines Moduls an der Detailseite eines Datensatzes, den ein anderes Modul führt (WebModuleSpec.relatedLinks). */
export interface RelatedLink extends Gate {
  /** '<modul>.<name>' */
  id: string
  /** Art des Datensatzes (RecordRef.type), an dessen Detailseite der Verweis erscheint: 'supplier', 'customer', 'item' */
  entity: string
  /** Verb + Objekt: „Einkauf ansehen“ */
  label: string
  icon?: IconComponent
  to: (id: string) => string
}

export interface DashboardWidget extends Gate {
  /** '<modul>.<name>' */
  id: string
  title: string
  lazy: () => Promise<{ default: ComponentType }>
  /** Breite im Raster der Startseite */
  size?: 'sm' | 'md' | 'lg'
  order?: number
}

export interface PaletteCommand extends Gate {
  /** '<modul>.<name>' */
  id: string
  /** deutsch, als Tätigkeit: „Korrekturbuchung erfassen“ */
  label: string
  /** weitere Suchwörter (Bruch, Schwund …) */
  keywords?: readonly string[]
  icon?: IconComponent
  /** Ziel der Navigation */
  to: string
}

/** Darstellung und Ziel eines Suchtreffers je Trefferart (`SearchHit.entity`). */
export interface SearchResultRenderer {
  /** deutsch, Gruppenüberschrift: „Artikel“ */
  label: string
  icon: IconComponent
  to(hit: SearchHit): string
}

/** Herkunft des Codes, den ein Scan-Ziel bekommt. */
export interface ScanOrigin {
  /**
   * true: Der Code ist das GS1-Datenelement seiner Art – die GTIN aus (01) für ein Artikel-Ziel, die Charge aus
   * (10) für ein Chargen-Ziel. Seine Art steht damit fest; ein Ziel darf ihn ohne Formprüfung annehmen.
   * false: getippt oder ein freier Code – die Art ist nur geraten (classifyScan), das Ziel prüft die Form selbst.
   */
  gs1: boolean
  /** Symbologie des Lesers ('ean_13', 'code_128', 'qr_code' …); 'unknown' bei getippten Codes und Tastatur-Scannern */
  format: string
}

export interface ScanHandler {
  /** Art des gescannten Codes */
  match: 'item-barcode' | 'document-number' | 'lot' | 'location' | 'bin'
  /** deutsch: „Bestand anzeigen“ */
  label: string
  to(code: string): string
  permission?: PermissionKey
  /** Funktionsschalter, der an sein muss (mehrere: alle) – sonst bietet die Hülle das Ziel nicht an */
  feature?: FeatureGate
  /**
   * Welche Codes dieses Ziel annimmt; ohne Angabe jeden Code der Art. `origin` sagt, ob die Art feststeht:
   * `accepts: (code, { gs1 }) => gs1 || LOT_NUMBER.test(code)` nimmt GS1-Chargen sicher und freie Codes nach Form.
   */
  accepts?(code: string, origin: ScanOrigin): boolean
}

/** Was die Hülle dem Panel des KI-Assistenten übergibt. */
export interface AssistantPanelProps {
  /** Panel schließen (⌘J, Schaltfläche „×“) */
  onClose: () => void
  /**
   * true: als Spalte angedockt (ab 1280 px) – das Panel zeichnet seinen Kopf selbst (Titel, Schließen).
   * false: im überlagernden Drawer bzw. Sheet – Titel und Schließen stellt die Hülle, das Panel lässt seinen Kopf weg.
   */
  docked: boolean
  /** Frage, die aus der Befehlspalette übergeben wurde – einmal übernehmen, dann onInitialMessageConsumed() */
  initialMessage: string | null
  onInitialMessageConsumed: () => void
}

/**
 * Panel des KI-Assistenten (design-system §3.6). Genau ein Modul meldet es an; die Hülle zeigt es rechts
 * angedockt bzw. als Drawer und öffnet es mit ⌘J. Ohne Anmeldung zeigt sie einen ruhigen Platzhalter.
 */
export interface AssistantPanelSlot extends Gate {
  lazy: () => Promise<{ default: ComponentType<AssistantPanelProps> }>
}

/** Was eine Nachschlage-Quelle bei jedem Aufruf erhält – die Auswahlfelder aus '@api' füllen es aus. */
export interface LookupRequest {
  /** lesende Aktion über den Client der Umgebung aufrufen (Rechte prüft der Server) */
  run<C extends AnyActionContract>(contract: C, input: InputArg<C>): Promise<OutputOf<C>>
  signal?: AbortSignal
  /** Standort, auf den sich Hinweise beziehen (Bestand am Standort des Belegs); null = alle */
  locationId: string | null
  /** frei je Auswahlfeld: { role: 'supplier' }, { documentType: 'order', date: '2026-10-12' } */
  params: Readonly<Record<string, unknown>>
  /** nächste Seite: `nextCursor` der vorigen Antwort */
  cursor?: string
}

/**
 * Nachschlage-Quelle für Auswahlfelder (ItemPicker, PartnerPicker, Scan-Ablauf – web/api/lookups). Module importieren
 * einander nicht: Das Stammdaten-Modul meldet seine Quellen hier an, alle anderen wählen über die Bausteine aus
 * '@api'. Feste Schlüssel: 'item' → ItemLookupEntry · 'partner' → PartnerLookupEntry · 'barcode' → BarcodeLookupHit
 * (Formen in '@api'); weitere Schlüssel sind frei ('<modul>.<gegenstand>').
 */
export interface LookupSource<T = unknown> extends Gate {
  /** Treffer zur Eingabe; die leere Eingabe liefert Vorschläge */
  search(query: string, request: LookupRequest): Promise<{ items: T[]; nextCursor?: string }>
  /** Eintrag zu einer gespeicherten Kennung (geöffneter Beleg) */
  byId?(id: string, request: LookupRequest): Promise<T | null>
  /** exakter Code (Barcode, Nummer) → Eintrag; null = unbekannt */
  resolve?(code: string, request: LookupRequest): Promise<T | null>
}

type LookupRowOf<C extends AnyActionContract> = OutputOf<C> extends { rows: readonly (infer R)[] } ? R : never

/**
 * Quelle aus einem Listen-Vertrag nach '@shared/list': gesucht wird mit `q`, geblättert per Cursor.
 *   lookups: { item: defineListLookup({ list: itemList, toEntry: (row) => ({ id: row.id, sku: row.sku, … }) }) }
 */
export function defineListLookup<C extends AnyActionContract, T>(spec: {
  list: C
  /** Zeile der Liste → Eintrag des Auswahlfelds */
  toEntry: (row: LookupRowOf<C>, request: LookupRequest) => T
  /** weitere Felder der Eingabe: Standort, Rolle, nur aktive … */
  input?: (query: string, request: LookupRequest) => Partial<InputArg<C>>
  /** Treffer je Seite, Standard 20 */
  limit?: number
  byId?: LookupSource<T>['byId']
  resolve?: LookupSource<T>['resolve']
  /** Standard: das Recht des Listen-Vertrags */
  permission?: PermissionKey
  feature?: string
}): LookupSource<T> {
  const { list, toEntry, input, limit = 20 } = spec
  const feature = spec.feature ?? list.feature
  return {
    permission: spec.permission ?? list.permission,
    ...(feature ? { feature } : {}),
    async search(query, request) {
      const q = query.trim()
      const page = (await request.run(list, {
        ...input?.(q, request),
        ...(q ? { q } : {}),
        page: { limit, ...(request.cursor ? { cursor: request.cursor } : {}) },
      } as InputArg<C>)) as { rows: LookupRowOf<C>[]; nextCursor?: string | null }
      return {
        items: page.rows.map((row) => toEntry(row, request)),
        ...(page.nextCursor ? { nextCursor: page.nextCursor } : {}),
      }
    },
    ...(spec.byId ? { byId: spec.byId } : {}),
    ...(spec.resolve ? { resolve: spec.resolve } : {}),
  }
}

/**
 * Was eine Probe der Hülle für den Start ohne Server liefert (docs/kasse-offline/SPEZIFIKATION.md §3.4, §14.7). Daraus
 * baut die Hülle eine Ersatz-Sitzung: diese Person, dieser Betrieb, dieser eine Standort mit genau diesen Rechten –
 * keine Navigation, keine anderen Rechte, keine Aktion des Servers (`actions` bleibt leer).
 */
export interface OfflineStartContext {
  /** wer am Gerät arbeitet (Kasse: Person aus dem Startpaket) */
  user: { id: string; displayName: string }
  /** Betrieb; `demo` steuert die Marke „Demo-Daten“ wie in der Sitzung */
  tenant: { id: string; name: string; demo: boolean }
  /** der eine Standort der Ersatz-Sitzung (Kasse: Standort der Kasse) */
  location: { id: string; code: string; name: string }
  /**
   * Rechte an diesem Standort – die Ersatz-Sitzung hat keine anderen; nie ein Platzhalter ('*'). Ohne Angabe: genau das
   * Recht der Route, in die die Probe führt.
   */
  permissions?: readonly PermissionKey[]
  /** Funktionsschalter des Betriebs (ohne Angabe: der Schalter der Route, falls sie einen hat) */
  features?: readonly string[]
  /** Schaltfläche auf der Fehlerseite anderer Routen, als Tätigkeit: „Zurück zur Kasse“ (Standard „Zurück“) */
  returnLabel?: string
  /** frei für das Modul (Kasse, Gerät …); die Hülle liest es nicht */
  data?: Readonly<Record<string, unknown>>
}

/**
 * Probe für den Start ohne Server: fragt das Gerät (IndexedDB), ob es ohne Server weiterarbeiten darf. null = nein.
 * `path` muss eine eigene Route des Moduls mit `layout: 'fullscreen'` sein (ohne Suchteil); andere Pfade verwirft die
 * Hülle. Die Probe soll schnell antworten (die Hülle wartet höchstens OFFLINE_PROBE_TIMEOUT_MS) und nie werfen.
 */
export type OfflineStartProbe = () => Promise<{ path: string; context: OfflineStartContext } | null>

/** Moduloption `offlineStart`: die Probe wird erst geladen, wenn die Sitzung ohne Antwort von Merxo scheitert. */
export interface OfflineStartSlot {
  lazy: () => Promise<{ default: OfflineStartProbe }>
}

export interface WebModuleSpec {
  /** Modul-ID = Ordnername */
  id: string
  routes?: readonly WebRoute[]
  nav?: readonly NavEntry[]
  widgets?: readonly DashboardWidget[]
  commands?: readonly PaletteCommand[]
  /**
   * Schlüssel = SearchHit.entity – oder `<entity>:<kind>` für eine Unterart (Belege: `document:<belegart>`), die
   * dem allgemeinen Schlüssel vorgeht. `document` ohne Belegart gehört der Hülle.
   */
  searchResults?: Readonly<Record<string, SearchResultRenderer>>
  /**
   * Verknüpfungen zwischen Datensätzen: Schlüssel = RecordRef.type (oder `<type>:<unterart>`), Wert = Pfad zum
   * Datensatz. Belege: je Belegart `document:<belegart>`; `document` selbst beantwortet die Hülle (/documents/:id).
   */
  entityLinks?: Readonly<Record<string, (id: string) => string>>
  /**
   * Verweise an den Detailseiten ANDERER Module: „Einkauf ansehen“ am Lieferanten (Stammdaten) führt in die
   * Übersicht des Einkaufs – ohne dass die Stammdaten den Einkauf kennen. Die Detailseite zeigt sie als
   * Nebenaktionen, sobald sie ihren Datensatz nennt (`<DetailPage related={{ type: 'supplier', id }}>`).
   */
  relatedLinks?: readonly RelatedLink[]
  scanHandlers?: readonly ScanHandler[]
  /** Panel des KI-Assistenten (höchstens ein Modul) */
  assistantPanel?: AssistantPanelSlot
  /** Nachschlage-Quellen für Auswahlfelder: Schlüssel = Art ('item', 'partner', 'barcode' …) */
  lookups?: Readonly<Record<string, LookupSource>>
  /**
   * Start ohne Server (Kasse ohne Verbindung, docs/kasse-offline/SPEZIFIKATION.md §14.7): Scheitert die Sitzung beim
   * Start ohne Antwort von Merxo (`network`, `timeout`, `foreign`), fragt die Hülle diese Probe – gleich auf welcher
   * Route. Liefert sie einen Kontext, leitet die Hülle auf `path` um und zeigt nur diese Route mit einer Ersatz-Sitzung.
   */
  offlineStart?: OfflineStartSlot
}

export interface WebModule {
  readonly kind: 'web-module'
  readonly id: string
  readonly routes: readonly WebRoute[]
  readonly nav: readonly NavEntry[]
  readonly widgets: readonly DashboardWidget[]
  readonly commands: readonly PaletteCommand[]
  readonly searchResults: Readonly<Record<string, SearchResultRenderer>>
  readonly entityLinks: Readonly<Record<string, (id: string) => string>>
  readonly relatedLinks: readonly RelatedLink[]
  readonly scanHandlers: readonly ScanHandler[]
  readonly assistantPanel?: AssistantPanelSlot
  readonly lookups?: Readonly<Record<string, LookupSource>>
  readonly offlineStart?: OfflineStartSlot
}

export function defineWebModule(spec: WebModuleSpec): WebModule {
  const fail = (message: string): never => {
    throw new Error(`[Oberflächen-Modul ${spec.id}] ${message}`)
  }
  if (!/^[a-z][a-z0-9]*$/.test(spec.id))
    fail('Die Modul-ID besteht aus Kleinbuchstaben und Ziffern (wie der Ordnername).')
  const paths = new Set<string>()
  for (const route of spec.routes ?? []) {
    if (!route.path.startsWith('/')) fail(`Der Pfad ${route.path} muss mit / beginnen.`)
    if (paths.has(route.path)) fail(`Der Pfad ${route.path} ist doppelt.`)
    paths.add(route.path)
  }
  // Belege gehören mehreren Modulen: Die Art 'document' ohne Belegart und die Route /documents/:id führt die Hülle
  // (sie schlägt die Belegart nach); ein Modul meldet seine Belegarten als 'document:<belegart>' an.
  if (paths.has('/documents/:id')) fail('Der Pfad /documents/:id gehört der Hülle (sie leitet nach Belegart weiter).')
  for (const [slot, keys] of [
    ['entityLinks', Object.keys(spec.entityLinks ?? {})],
    ['searchResults', Object.keys(spec.searchResults ?? {})],
  ] as const) {
    if (keys.includes('document'))
      fail(`${slot}.document gehört der Hülle – melden Sie je Belegart '${"document:<belegart>"}' an (docs/MODULES.md §6).`)
  }
  // Start ohne Server führt nur in eine eigene Vollbild-Route (ohne App-Rahmen, ohne Navigation)
  if (spec.offlineStart && !(spec.routes ?? []).some((route) => route.layout === 'fullscreen'))
    fail('offlineStart braucht eine eigene Route mit layout: fullscreen.')
  for (const item of [...(spec.widgets ?? []), ...(spec.commands ?? []), ...(spec.relatedLinks ?? [])]) {
    if (!item.id.startsWith(`${spec.id}.`)) fail(`Die Kennung ${item.id} muss mit '${spec.id}.' beginnen.`)
  }
  return Object.freeze({
    kind: 'web-module' as const,
    id: spec.id,
    routes: Object.freeze([...(spec.routes ?? [])]),
    nav: Object.freeze([...(spec.nav ?? [])]),
    widgets: Object.freeze([...(spec.widgets ?? [])]),
    commands: Object.freeze([...(spec.commands ?? [])]),
    searchResults: Object.freeze({ ...spec.searchResults }),
    entityLinks: Object.freeze({ ...spec.entityLinks }),
    relatedLinks: Object.freeze([...(spec.relatedLinks ?? [])]),
    scanHandlers: Object.freeze([...(spec.scanHandlers ?? [])]),
    ...(spec.assistantPanel ? { assistantPanel: Object.freeze({ ...spec.assistantPanel }) } : {}),
    ...(spec.lookups ? { lookups: Object.freeze({ ...spec.lookups }) } : {}),
    ...(spec.offlineStart ? { offlineStart: Object.freeze({ ...spec.offlineStart }) } : {}),
  })
}

export const isWebModule = (value: unknown): value is WebModule =>
  typeof value === 'object' && value !== null && (value as { kind?: unknown }).kind === 'web-module'
